import type { DataViewFilter } from './types';

function toComparableList(value: unknown): string[] {
    if (value === null || value === undefined) {
        return [];
    }
    return Array.isArray(value) ? value.map(String) : [String(value)];
}

function isSameDay(timestamp: unknown, day: string): boolean {
    if (timestamp === null || timestamp === undefined) {
        return false;
    }
    const date = new Date(timestamp as string | number);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === day.slice(0, 10);
}

function isInRange(value: unknown, range: { gte?: number; lte?: number }): boolean {
    if (value === null || value === undefined) {
        return false;
    }
    const numeric = typeof value === 'number' ? value : new Date(value as string).getTime();
    if (Number.isNaN(numeric)) {
        return false;
    }
    if (range.gte !== undefined && numeric < range.gte) {
        return false;
    }
    return !(range.lte !== undefined && numeric > range.lte);
}

/**
 * Le matching standard d'un filtre sur une ligne, aligné sur celui de la `DataTable`. Une valeur
 * d'accesseur absente ne matche aucun filtre actif : sélectionner une organisation ne doit pas faire
 * remonter les lignes qui n'en ont pas.
 */
export function matchesFilter<TData>(filter: DataViewFilter<TData>, row: TData, value: unknown): boolean {
    if (filter.matches) {
        return filter.matches(row, value);
    }
    if (!filter.accessor) {
        return true;
    }

    const rowValue = filter.accessor(row);

    switch (filter.type) {
        case 'select':
        case 'multi-select':
        case 'async-multi-select': {
            const selected = toComparableList(value);
            if (selected.length === 0) {
                return true;
            }
            // Un accesseur peut rendre une liste (les tags d'un produit) : l'intersection suffit.
            return toComparableList(rowValue).some((candidate) => selected.includes(candidate));
        }
        case 'text':
            return String(rowValue ?? '')
                .toLowerCase()
                .includes(String(value).toLowerCase());
        case 'number':
            return Number(rowValue) === Number(value);
        case 'boolean':
            return Boolean(rowValue) === Boolean(value);
        case 'date':
            return isSameDay(rowValue, String(value));
        case 'date-range':
        case 'number-range':
            return isInRange(rowValue, value as { gte?: number; lte?: number });
        default:
            return true;
    }
}

type ApplyDataViewFiltersParams<TData> = {
    rows: TData[];
    filters: DataViewFilter<TData>[];
    filterState: Record<string, unknown>;
    searchValue: string;
    searchAccessor?: (row: TData) => (string | null | undefined)[];
};

/**
 * Applique la recherche et les filtres qui portent un `accessor`. Les autres sont des filtres serveur :
 * la `DataView` les laisse passer, c'est la query de la page qui les a déjà appliqués.
 */
export function applyDataViewFilters<TData>({
    rows,
    filters,
    filterState,
    searchValue,
    searchAccessor,
}: ApplyDataViewFiltersParams<TData>): TData[] {
    const activeFilters = filters.filter((filter) => {
        const value = filterState[filter.key];
        if (value === undefined || value === null || value === '') {
            return false;
        }
        if (Array.isArray(value) && value.length === 0) {
            return false;
        }
        return Boolean(filter.accessor || filter.matches);
    });

    const search = searchValue.trim().toLowerCase();
    if (activeFilters.length === 0 && (!search || !searchAccessor)) {
        return rows;
    }

    return rows.filter((row) => {
        if (search && searchAccessor) {
            const haystack = searchAccessor(row).filter(Boolean) as string[];
            if (!haystack.some((field) => field.toLowerCase().includes(search))) {
                return false;
            }
        }
        return activeFilters.every((filter) => matchesFilter(filter, row, filterState[filter.key]));
    });
}
